import { useState, useEffect, useCallback } from 'react';
import { sitesService, Site, Location, Equipment } from '../services/sitesService';

interface StandardTask {
  id: number;
  task_name: string;
  task_code: string;
  task_type: string;
  equipment: number;
  equipment_number: string;
  description: string;
  risk_level: string;
  permit_type_required: string;
  requires_isolation: boolean;
  estimated_duration_hours: number;
  crew_size_required: number;
  high_risk_activities: string[];
}

interface SafetyMeasure {
  id: number;
  measure_type: 'iasm' | 'rasm' | 'general';
  sequence_number: number;
  description: string;
  implementation_notes: string;
  verification_required: boolean;
  verification_method: string;
  can_be_removed_for_testing: boolean;
  removal_conditions: string;
}

interface IsolationMethod {
  id: number;
  method_name: string;
  sequence_number: number;
  description: string;
  isolation_type: string;
  requires_verification: boolean;
  verification_method: string;
  tools_required: string;
  ppe_required: string;
  competency_required: string;
  estimated_time_minutes: number;
}

interface PrepopulateData {
  equipment: Equipment;
  selected_task: StandardTask | null;
  suggested_permit_type: string;
  suggested_isolation_required: boolean;
  suggested_high_risk_activities: string[];
  suggested_safety_measures: SafetyMeasure[];
  suggested_isolation_methods: IsolationMethod[];
  suggested_work_description: string;
  suggested_ppe_requirements: string;
  suggested_crew_size: number;
  suggested_duration_hours: number;
}

export const useSiteEquipment = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [standardTasks, setStandardTasks] = useState<StandardTask[]>([]);
  const [prepopulateData, setPrepopulateData] = useState<PrepopulateData | null>(null);
  
  const [selectedSite, setSelectedSite] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<number | null>(null);
  const [selectedTask, setSelectedTask] = useState<number | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all sites on component mount
  useEffect(() => {
    fetchSites();
  }, []);

  // When site changes, fetch locations and equipment
  useEffect(() => {
    if (selectedSite) {
      fetchLocations(selectedSite);
      fetchEquipment(selectedSite);
    } else {
      setLocations([]);
      setEquipment([]);
      setSelectedLocation(null);
      setSelectedEquipment(null);
    }
  }, [selectedSite]);

  // Clear tasks when equipment changes (for now)
  useEffect(() => {
    if (selectedEquipment) {
      // TODO: Implement standard tasks fetching when API is available
      setStandardTasks([]);
    } else {
      setStandardTasks([]);
      setSelectedTask(null);
    }
  }, [selectedEquipment]);

  const fetchSites = useCallback(async () => {
    try {
      setLoading(true);
      const sitesData = await sitesService.getSites();
      setSites(sitesData);
      
      // Auto-select if there's only one site and no site is currently selected
      if (sitesData.length === 1 && selectedSite === null) {
        console.log('🔧 Auto-selecting single site:', sitesData[0].name, '(ID:', sitesData[0].id, ')');
        setSelectedSite(sitesData[0].id);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError('Failed to load sites. Please check your authentication.');
    } finally {
      setLoading(false);
    }
  }, [selectedSite]);

  const fetchLocations = useCallback(async (siteId: number) => {
    try {
      setLoading(true);
      const locationsData = await sitesService.getLocationsBySite(siteId);
      setLocations(locationsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to load locations for this site.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEquipment = useCallback(async (siteId: number) => {
    try {
      setLoading(true);
      const equipmentData = await sitesService.getEquipmentBySite(siteId);
      setEquipment(equipmentData);
      setError(null);
    } catch (err) {
      console.error('Error fetching equipment:', err);
      setError('Failed to load equipment for this site.');
    } finally {
      setLoading(false);
    }
  }, []);

  // New method to fetch all equipment for equipment-first selection
  const fetchAllEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const equipmentData = await sitesService.getEquipment();
      setEquipment(equipmentData);
      setError(null);
    } catch (err) {
      console.error('Error fetching all equipment:', err);
      setError('Failed to load equipment.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStandardTasks = useCallback(async (equipmentId: number) => {
    try {
      setLoading(true);
      // TODO: Implement when standard tasks API is available
      setStandardTasks([]);
      setError(null);
    } catch (err) {
      console.error('Error fetching standard tasks:', err);
      setError('Failed to load standard tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPrepopulateData = useCallback(async (equipmentId: number, taskId?: number | null) => {
    try {
      setLoading(true);
      // For now, create basic prepopulate data from selected equipment
      const selectedEquipmentData = equipment.find((eq: Equipment) => eq.id === equipmentId);
      if (selectedEquipmentData) {
        const basicPrepopulateData: PrepopulateData = {
          equipment: selectedEquipmentData,
          selected_task: null,
          suggested_permit_type: selectedEquipmentData.requires_isolation ? 'work_authority' : 'general_work',
          suggested_isolation_required: selectedEquipmentData.requires_isolation,
          suggested_high_risk_activities: selectedEquipmentData.has_high_risk_energy ? ['electrical'] : [],
          suggested_safety_measures: [],
          suggested_isolation_methods: [],
          suggested_work_description: `Work on ${selectedEquipmentData.name}`,
          suggested_ppe_requirements: 'Standard PPE required',
          suggested_crew_size: 1,
          suggested_duration_hours: 4
        };
        setPrepopulateData(basicPrepopulateData);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching prepopulate data:', err);
      setError('Failed to load equipment data');
    } finally {
      setLoading(false);
    }
  }, [equipment]);

  const handleSiteChange = useCallback((siteId: number | null) => {
    setSelectedSite(siteId);
    setSelectedLocation(null);
    setSelectedEquipment(null);
    setSelectedTask(null);
    setPrepopulateData(null);
  }, []);

  const handleLocationChange = useCallback((locationId: number | null) => {
    setSelectedLocation(locationId);
    setSelectedEquipment(null);
    setSelectedTask(null);
    setPrepopulateData(null);
  }, []);

  const handleEquipmentChange = useCallback((equipmentId: number | null) => {
    setSelectedEquipment(equipmentId);
    setSelectedTask(null);
    
    // Auto-populate site when equipment is selected
    if (equipmentId) {
      const selectedEquipmentData = equipment.find((eq: Equipment) => eq.id === equipmentId);
      if (selectedEquipmentData) {
        // Find the site that this equipment belongs to
        const equipmentSite = sites.find((site: Site) => site.code === selectedEquipmentData.site_code);
        if (equipmentSite && equipmentSite.id !== selectedSite) {
          setSelectedSite(equipmentSite.id);
        }
      }
      fetchPrepopulateData(equipmentId, null);
    } else {
      setPrepopulateData(null);
    }
  }, [equipment, sites, selectedSite, fetchPrepopulateData]);

  const handleTaskChange = useCallback((taskId: number | null) => {
    setSelectedTask(taskId);
    if (selectedEquipment) {
      fetchPrepopulateData(selectedEquipment, taskId);
    }
  }, [selectedEquipment, fetchPrepopulateData]);

  const resetSelection = useCallback(() => {
    setSelectedSite(null);
    setSelectedLocation(null);
    setSelectedEquipment(null);
    setSelectedTask(null);
    setPrepopulateData(null);
  }, []);

  // Get the selected equipment object
  const getSelectedEquipment = useCallback(() => {
    return equipment.find((eq: Equipment) => eq.id === selectedEquipment) || null;
  }, [equipment, selectedEquipment]);

  // Get the selected task object
  const getSelectedTask = useCallback(() => {
    return standardTasks.find((task: StandardTask) => task.id === selectedTask) || null;
  }, [standardTasks, selectedTask]);

  return {
    // Data
    sites,
    locations,
    equipment,
    standardTasks,
    prepopulateData,

    // Selected values
    selectedSite,
    selectedLocation,
    selectedEquipment,
    selectedTask,

    // State
    loading,
    error,

    // Actions
    setSelectedSite,
    handleSiteChange,
    handleLocationChange,
    handleEquipmentChange,
    handleTaskChange,
    resetSelection,
    
    // Helpers
    getSelectedEquipment,
    getSelectedTask,
    
    // Manual fetch functions
    fetchSites,
    fetchLocations,
    fetchEquipment,
    fetchAllEquipment,
    fetchStandardTasks,
    fetchPrepopulateData,
  };
}; 